import { Check, Loader2, TriangleAlert, UploadCloud, X } from "lucide-react";
import { useRef } from "react";
import type { SceneDto, SceneStatusDto } from "../../shared/api-types";
import type { Dictionary } from "../i18n/de";
import { pipelineError, useLocale, useT } from "../i18n";

/** Eine Datei auf ihrem Weg in den Storage — lebt nur im Browser, bis die Charge abgeräumt wird. */
export type UploadEntry = {
  sceneId: string;
  /** Datei + Ziel-URL bleiben liegen, damit „Erneut versuchen“ ohne neues Auswählen geht */
  file: File;
  uploadUrl: string;
  name: string;
  bytes: number;
  loaded: number;
  /** Objekt-URL des lokalen Bildes: die Karte zeigt es sofort, lange vor dem Render */
  preview: string;
  state: "waiting" | "uploading" | "done" | "error";
  /** bereits übersetzte Meldung */
  error?: string;
  /** Bildtausch statt neuer Szene: abbrechen heißt hier NICHT die Szene löschen */
  replacing?: true;
};

/** Datei, die es gar nicht erst zur Szene geschafft hat (falscher Typ, zu groß, Anlegen fehlgeschlagen) */
export type RejectedFile = { key: string; name: string; error: string };

export type ProgressState = {
  label: string;
  /** 0–100; null = unbestimmt (Warteschlange, Verarbeitung ohne Meldung) */
  percent: number | null;
  tone: "upload" | "process" | "error" | "done";
};

/**
 * Der eine Ort, an dem „wo steht diese Szene gerade?“ entschieden wird — Karte und
 * Sammelleiste zeigen sonst leicht Unterschiedliches an.
 */
export function progressState(
  t: Dictionary,
  scene: SceneDto | undefined,
  status: SceneStatusDto | undefined,
  entry: UploadEntry | undefined,
): ProgressState {
  if (entry && entry.state !== "done") {
    if (entry.state === "error") {
      return {
        label: entry.error ?? t.pipeline.failed,
        percent: null,
        tone: "error",
      };
    }
    const pct = entry.bytes > 0 ? Math.round((entry.loaded / entry.bytes) * 100) : 0;
    return entry.state === "waiting"
      ? { label: t.upload.stateWaiting, percent: 0, tone: "upload" }
      : { label: t.upload.stateUploading(pct), percent: pct, tone: "upload" };
  }
  const sceneStatus = status?.status ?? scene?.status;
  if (sceneStatus === "error") {
    const raw = status?.processError ?? scene?.processError ?? null;
    return {
      label: pipelineError(t, raw) ?? t.pipeline.failed,
      percent: null,
      tone: "error",
    };
  }
  if (sceneStatus === "ready") {
    return { label: t.upload.stateReady, percent: 100, tone: "done" };
  }
  // Szene steht auf "uploading", aber es lädt nichts (mehr): abgebrochener Upload
  // aus einer früheren Sitzung — die Datei ist weg, hier hilft nur Löschen.
  if (sceneStatus === "uploading" && !entry) {
    return { label: t.apiError.uploadFailed, percent: null, tone: "error" };
  }
  // status="uploading" mit fertigem Upload = der lokale Stand ist nur noch nicht
  // nachgeladen — für die Anzeige dasselbe wie "processing".
  if (status?.queuePos) {
    return {
      label: t.upload.stateQueued(status.queuePos),
      percent: null,
      tone: "process",
    };
  }
  const progress = status?.progress ?? scene?.progress ?? 0;
  const phase = status?.phase ?? scene?.phase ?? null;
  const label =
    phase === "analyze"
      ? t.upload.phaseAnalyze
      : phase === "tiles"
        ? t.upload.phaseTiles
        : phase === "bases"
          ? t.upload.phaseBases
          : phase === "poster"
            ? t.upload.phasePoster
            : t.upload.stateProcessing;
  return { label, percent: progress > 0 ? progress : null, tone: "process" };
}

function formatBytes(bytes: number, locale: string): string {
  const mb = bytes / 1024 / 1024;
  if (mb >= 1024) {
    return `${(mb / 1024).toLocaleString(locale, { maximumFractionDigits: 1 })} GB`;
  }
  return `${mb.toLocaleString(locale, { maximumFractionDigits: mb < 10 ? 1 : 0 })} MB`;
}

const toneBar = {
  upload: "bg-accent",
  process: "bg-accent",
  error: "bg-red-500",
  done: "bg-emerald-500",
} as const;

/** Schmaler Fortschrittsbalken — auch von den Szenen-Karten benutzt. */
export function ProgressBar({
  percent,
  tone,
  thin = false,
  className = "",
}: {
  percent: number | null;
  tone: ProgressState["tone"];
  /** Schmale Variante für die Szenen-Karten */
  thin?: boolean;
  className?: string;
}) {
  return (
    <div
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={percent === null ? undefined : Math.round(percent)}
      className={`${thin ? "h-1.5" : "h-2.5"} overflow-hidden rounded-full bg-zinc-200 dark:bg-zinc-800 ${className}`}
    >
      {percent === null ? (
        // Unbestimmt: schmaler Balken, der hin- und herläuft (Warteschlange)
        <div className={`h-full w-1/3 progress-indeterminate rounded-full ${toneBar[tone]}`} />
      ) : (
        <div
          className={`h-full rounded-full transition-[width] duration-300 ${toneBar[tone]}`}
          style={{ width: `${Math.max(2, percent)}%` }}
        />
      )}
    </div>
  );
}

/**
 * Sammelanzeige über der Szenen-Liste: eine Zeile für den Upload, eine für die
 * Verarbeitung, darunter auf Wunsch jede Datei einzeln. Bewusst hier auf der
 * Tour-Seite verankert statt global — Uploads laufen nur hier, und der Zustand
 * bleibt damit da, wo er entsteht.
 */
export function UploadPanel({
  batch,
  uploads,
  rejected,
  scenes,
  sceneStatus,
  onDismiss,
}: {
  /** Szenen-IDs dieser Charge, in Reihenfolge des Hinzufügens */
  batch: string[];
  uploads: Record<string, UploadEntry>;
  rejected: RejectedFile[];
  scenes: SceneDto[];
  sceneStatus: Record<string, SceneStatusDto>;
  onDismiss: () => void;
}) {
  const t = useT();
  const [locale] = useLocale();
  // Startpunkt für die Restzeit — erst ab dem ersten echten Fortschritt messen,
  // sonst verfälscht der Verbindungsaufbau die Rate.
  const rateAnchor = useRef<{ at: number; bytes: number } | null>(null);

  const sceneById = new Map(scenes.map((s) => [s.id, s]));
  const rows = batch
    .map((id) => ({ id, scene: sceneById.get(id), entry: uploads[id] }))
    .filter((r) => r.scene ?? r.entry);

  const entries = rows.map((r) => r.entry).filter((e): e is UploadEntry => e !== undefined);
  const totalBytes = entries.reduce((sum, e) => sum + e.bytes, 0);
  const loadedBytes = entries.reduce(
    (sum, e) => sum + (e.state === "done" ? e.bytes : e.loaded),
    0,
  );
  const uploadedCount = entries.filter((e) => e.state === "done").length;
  const uploading = entries.some((e) => e.state === "waiting" || e.state === "uploading");

  const processTotal = rows.filter((r) => r.scene).length;
  const processedCount = rows.filter(
    (r) => (sceneStatus[r.id]?.status ?? r.scene?.status) === "ready",
  ).length;
  const processPercent =
    processTotal === 0
      ? 0
      : rows.reduce((sum, r) => {
          const state = progressState(t, r.scene, sceneStatus[r.id], r.entry);
          if (state.tone === "done") return sum + 100;
          if (state.tone === "error") return sum + 100; // erledigt, wenn auch erfolglos
          if (state.tone === "upload") return sum;
          return sum + (state.percent ?? 0);
        }, 0) / processTotal;

  // Fehler zählen nur mit — den Zustand samt „Erneut versuchen“ zeigt die Karte, die
  // Summe das rote Banner. Eine Liste hier wäre die dritte Anzeige; abgelehnte
  // Dateien (falscher Typ, zu groß) gehen im Zähler auf.
  const failedScenes = rows.filter(
    (r) => progressState(t, r.scene, sceneStatus[r.id], r.entry).tone === "error",
  ).length;
  const failureCount = rejected.length + failedScenes;

  const settled =
    !uploading &&
    rows.every((r) => {
      const s = sceneStatus[r.id]?.status ?? r.scene?.status;
      return s === undefined || s === "ready" || s === "error";
    });

  if (rows.length === 0 && rejected.length === 0) return null;

  // Restzeit nur während des Uploads und erst, wenn eine belastbare Rate vorliegt
  let eta: string | null = null;
  if (uploading && loadedBytes > 0) {
    rateAnchor.current ??= { at: Date.now(), bytes: loadedBytes };
    const elapsed = (Date.now() - rateAnchor.current.at) / 1000;
    const moved = loadedBytes - rateAnchor.current.bytes;
    if (elapsed > 2 && moved > 0) {
      const seconds = ((totalBytes - loadedBytes) / (moved / elapsed)) | 0;
      if (seconds > 5) {
        eta = t.upload.remaining(
          seconds >= 90 ? t.upload.minutes(Math.ceil(seconds / 60)) : t.upload.seconds(seconds),
        );
      }
    }
  } else if (!uploading) {
    rateAnchor.current = null;
  }

  return (
    <div className="mb-5 rounded-2xl border border-zinc-200 bg-white p-5 dark:border-zinc-800 dark:bg-zinc-900">
      <div className="flex items-center gap-2">
        {settled ? (
          failureCount > 0 ? (
            <TriangleAlert className="size-4 shrink-0 text-amber-500" />
          ) : (
            <Check className="size-4 shrink-0 text-emerald-500" />
          )
        ) : (
          <Loader2 className="size-4 shrink-0 animate-spin text-accent" />
        )}
        <span className="text-base font-semibold">
          {!settled
            ? uploading
              ? t.upload.titleUploading
              : t.upload.titleProcessing
            : processTotal > 0
              ? t.upload.allDone(processedCount)
              : t.upload.failed(failureCount)}
        </span>
        {failureCount > 0 && processTotal > 0 && (
          <span className="text-sm text-red-600 dark:text-red-400">
            · {t.upload.failed(failureCount)}
          </span>
        )}
        <span className="ml-auto flex items-center gap-1">
          {settled && (
            <button
              title={t.upload.dismiss}
              onClick={onDismiss}
              className="rounded-md p-1 text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-zinc-700 dark:hover:bg-zinc-800 dark:hover:text-zinc-200"
            >
              <X className="size-4" />
            </button>
          )}
        </span>
      </div>

      {!settled && (
        <div className="mt-5 space-y-5">
          {entries.length > 0 && (
            <div>
              <div className="mb-2 flex items-center gap-2 text-sm text-zinc-600 dark:text-zinc-300">
                <UploadCloud className="size-4" />
                <span>{t.upload.uploading(uploadedCount, entries.length)}</span>
                <span className="ml-auto tabular-nums">
                  {formatBytes(loadedBytes, locale)} / {formatBytes(totalBytes, locale)}
                  {eta && ` · ${eta}`}
                </span>
              </div>
              <ProgressBar
                percent={totalBytes > 0 ? (loadedBytes / totalBytes) * 100 : 0}
                tone="upload"
              />
            </div>
          )}
          {processTotal > 0 && (
            <div>
              <div className="mb-2 flex items-center gap-2 text-sm text-zinc-600 dark:text-zinc-300">
                <Loader2 className="size-4" />
                <span>{t.upload.processing(processedCount, processTotal)}</span>
              </div>
              <ProgressBar percent={processPercent} tone="process" />
            </div>
          )}
        </div>
      )}

    </div>
  );
}
